import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, y = 28, className = "", ...rest }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/* Signature on-load moment: masked line-by-line headline reveal */
export function MaskedLines({ lines, delay = 0, className = "" }) {
  return (
    <span className={`block ${className}`}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em]">
          <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: delay + i * 0.13, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, description, align = "left", id }) {
  return (
    <Reveal className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow && (
        <p className="eyebrow mb-4" data-testid={id ? `${id}-eyebrow` : undefined}>
          {eyebrow}
        </p>
      )}
      <h2 className="font-display text-3xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-4xl lg:text-5xl">
        {title}
      </h2>
      {description && <p className="mt-5 text-base leading-relaxed text-slate-300 sm:text-lg">{description}</p>}
    </Reveal>
  );
}

function CountUp({ to, active, duration = 1.8 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!active || !ref.current) return;
    const controls = animate(0, to, {
      duration,
      ease: EASE,
      onUpdate: (v) => {
        if (ref.current) ref.current.textContent = Math.round(v).toString();
      },
    });
    return () => controls.stop();
  }, [active, to, duration]);
  return <span ref={ref}>0</span>;
}

export function StatCircle({ value, suffix = "%", label, sub, testid, tone = "#38BDF8", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const r = 84;
  const C = 2 * Math.PI * r;
  const target = Math.min(value, 100);
  return (
    <div ref={ref} className="flex flex-col items-center text-center" data-testid={testid}>
      <div className="relative h-44 w-44 sm:h-52 sm:w-52">
        <svg viewBox="0 0 200 200" className="h-full w-full -rotate-90">
          <circle cx="100" cy="100" r={r} fill="none" stroke="rgba(148,163,184,0.14)" strokeWidth="10" />
          <motion.circle
            cx="100"
            cy="100"
            r={r}
            fill="none"
            stroke={tone}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            animate={inView ? { strokeDashoffset: C - (C * target) / 100 } : {}}
            transition={{ duration: 1.8, delay, ease: EASE }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-display text-5xl font-black text-white sm:text-6xl">
            <CountUp to={value} active={inView} />
            {suffix}
          </span>
        </div>
      </div>
      <p className="mt-5 max-w-[250px] text-sm leading-relaxed text-slate-300">{label}</p>
      {sub && <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{sub}</p>}
    </div>
  );
}

export function Marquee({ items }) {
  const row = (hidden) => (
    <div className="flex shrink-0" aria-hidden={hidden || undefined}>
      {items.map((it, i) => (
        <span key={`${it}-${i}`} className="flex items-center gap-8 pr-8">
          <span className="whitespace-nowrap font-display text-xl font-bold uppercase tracking-wide text-slate-200 sm:text-2xl">
            {it}
          </span>
          <span className="h-2 w-2 rotate-45 bg-ice/80" />
        </span>
      ))}
    </div>
  );
  return (
    <div className="marquee relative overflow-hidden border-y border-white/10 bg-navy-800 py-4">
      <div className="marquee-track flex w-max">
        {row(false)}
        {row(true)}
      </div>
    </div>
  );
}

export function Countdown({ target, testid }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const cells = [
    [Math.floor(diff / 86400000), "Days"],
    [Math.floor((diff % 86400000) / 3600000), "Hrs"],
    [Math.floor((diff % 3600000) / 60000), "Min"],
    [Math.floor((diff % 60000) / 1000), "Sec"],
  ];
  return (
    <div className="flex gap-3 sm:gap-4" data-testid={testid}>
      {cells.map(([v, l]) => (
        <div key={l} className="glass min-w-[72px] rounded-xl px-4 py-3 text-center sm:px-6 sm:py-4">
          <div className="font-display text-3xl font-black tabular-nums text-white sm:text-4xl">
            {String(v).padStart(2, "0")}
          </div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{l}</div>
        </div>
      ))}
    </div>
  );
}

export function PageHero({ eyebrow, lines, sub, children, testid }) {
  return (
    <section className="bg-grid relative overflow-hidden pb-16 pt-36 sm:pt-44" data-testid={testid}>
      <div className="absolute -top-32 right-[-120px] h-[480px] w-[480px] rounded-full bg-ice/10 blur-[140px]" />
      <div className="absolute bottom-[-160px] left-[-80px] h-[380px] w-[380px] rounded-full bg-crimson/10 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="eyebrow mb-6"
        >
          {eyebrow}
        </motion.p>
        <h1 className="font-display text-5xl font-black uppercase leading-[0.9] text-white sm:text-7xl">
          <MaskedLines lines={lines} delay={0.2} />
        </h1>
        {sub && (
          <Reveal delay={0.4}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{sub}</p>
          </Reveal>
        )}
        {children}
      </div>
    </section>
  );
}
